// Nutzungs-Alerts nach #vertrieb, sobald ein Kunde 80 oder 100 Prozent eines
// Limits erreicht (D13). Es gibt genau einen Auslöser, <PricingUsageWatcher />
// aus use-usage-alerts.js unter PaymentContextProvider.
//
// Gesendet wird nur in enforced, nie für Admins und nie in einer Simulation,
// auf keiner Stage. Anders als bei „Paket anfragen" gibt es also auch auf
// develop und staging keinen Testversand über die Simulation. Je Ressource
// geht nur die höchste erreichte Schwelle raus. Ein Alert gilt 30 Tage je
// Stage, Kunde, Ressource, Schwelle, Payment und Limit. Ein neues Limit (etwa
// eine Zusatzstelle) schaltet ihn wieder scharf. Eine höhere Schwelle deckt
// die niedrigere mit ab, nach „voll" kommt also kein Alert der 80er-Schwelle
// mehr, wenn der Kunde etwas löscht.
//
// Dedupe wie bei den Paketanfragen in pricing-slack.js. Zuerst der Cooldown im
// Browser, vor dem Senden markiert. Danach logsByPartner auf einen Eintrag
// PRICING_THRESHOLD_ALERT mit demselben Key, damit ein zweites Teammitglied
// keinen zweiten Alert auslöst. Nach dem Senden schreibt createLog den
// Eintrag mit 30 Tagen ttl. Ein Fehler beim Versand, auch eine Antwort von
// Slack außerhalb von 2xx, entfernt die Markierung wieder. Fehler beim Lesen
// oder Schreiben des Logs halten den Alert nie auf.
//
// Senden darf nur, wer den Log auch schreiben darf (canWritePartnerLog), also
// nicht VIEWER. Sonst ginge der Alert ohne Log raus, und jeder weitere
// Browser des Kunden schickte ihn erneut. Anders als bei „Paket anfragen"
// wartet hier niemand auf einen Klick, der Alert kommt dann mit dem nächsten
// Teammitglied, das Logs anlegen darf.
//
// Die Texte sind interne Copy für den Vertrieb und deshalb fest Deutsch wie
// in helpers/payment-alert.js, ohne i18n.
//
// Zieht über pricing-slack.js helpers/index.js und graphql/index.js und damit
// aws-amplify. Tests mocken beide (usage-alerts.test.js).
import { getAivyOsCustomerUrl, sendSlackMessage } from '../../helpers'
import { localStorage } from '../../helpers/local-storage'
import {
  contextLine,
  createCooldownStore,
  headlineEmoji,
  stagePrefix
} from '../../helpers/slack-alert-kit'
import { STAGE } from '../../helpers/stage'
import { MODES } from './entitlements'
import { LIMIT_STATES, resolveLimitState } from './limits'
import {
  assertSlackAccepted,
  formatCount,
  hasRecentPartnerLog,
  planText,
  writePartnerLog
} from './pricing-slack'

export const USAGE_ALERT_RESOURCES = Object.freeze([
  'careers',
  'seats',
  'talentStorage'
])

// Schwellen in Prozent, aufsteigend
export const USAGE_ALERT_THRESHOLDS = Object.freeze({
  WARNING: 80,
  FULL: 100
})

const THRESHOLD_ORDER = [
  USAGE_ALERT_THRESHOLDS.WARNING,
  USAGE_ALERT_THRESHOLDS.FULL
]

export const USAGE_ALERT_RESULTS = Object.freeze({
  SENT: 'SENT',
  DUPLICATE: 'DUPLICATE',
  SKIPPED: 'SKIPPED'
})

// event_name der Log-Einträge für die Dedupe über alle Nutzer eines Kunden
export const PRICING_THRESHOLD_LOG_EVENT = 'PRICING_THRESHOLD_ALERT'

// localStorage-Record { dedupeKey: sentAt }, beim Schreiben auf 30 Tage
// gekürzt
const USAGE_ALERT_COOLDOWN = 'PRICING_USAGE_ALERT_COOLDOWN'
export const USAGE_ALERT_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000

const TRIGGER = 'ausgelöst durch die Nutzungsprüfung im Dashboard'

// Der Wrapper aus helpers/local-storage meldet Speicherfehler an Bugsnag, die
// Session-Map im Kit trägt den Cooldown dann weiter.
const cooldown = createCooldownStore({
  storageKey: USAGE_ALERT_COOLDOWN,
  cooldownMs: USAGE_ALERT_COOLDOWN_MS,
  storage: localStorage
})

// ---------------------------------------------------------------- Deskriptor

const isCount = (value) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0

/**
 * Höchste erreichte Schwelle oder null. Nur für ein endliches, bekanntes
 * Limit über 0 und eine gezählte Nutzung. Ob 80 Prozent erreicht sind,
 * entscheidet wie bei den Metern resolveLimitState (WARNING_RATIO).
 *
 * @param {{ limit: number|null, used: number|null }} usage
 * @returns {80|100|null}
 */
export const usageAlertThreshold = ({ limit, used } = {}) => {
  if (!isCount(limit) || limit <= 0 || !isCount(used)) return null

  const { state } = resolveLimitState({ limit, used })
  if (state === LIMIT_STATES.AT_LIMIT || state === LIMIT_STATES.OVER_LIMIT) {
    return USAGE_ALERT_THRESHOLDS.FULL
  }
  if (state === LIMIT_STATES.WARNING) return USAGE_ALERT_THRESHOLDS.WARNING
  return null
}

/**
 * Darf für diesen Kunden überhaupt ein Alert entstehen? Nur in enforced mit
 * geklärten Entitlements, nie für Admins und nie in einer Simulation.
 *
 * @param {{ entitlements: object, isAdmin?: boolean, partnerId?: string }}
 *   params
 */
export const isUsageAlertEligible = ({ entitlements, isAdmin, partnerId }) =>
  !!partnerId &&
  entitlements?.mode === MODES.ENFORCED &&
  !!entitlements.settled &&
  !isAdmin &&
  !entitlements.isAdmin &&
  !entitlements.isSimulated

const limitOf = (entitlements, resource) => {
  const limit = entitlements?.limits?.[resource]?.limit
  return typeof limit === 'number' ? limit : null
}

/**
 * Alerts, die für den aktuellen Stand fällig wären, je Ressource höchstens
 * einer. Rein, ob schon gesendet wurde, prüft maybeSendUsageAlert.
 *
 * @param {object} params
 * @param {object} params.entitlements
 * @param {boolean} [params.isAdmin]
 * @param {object} params.partner
 * @param {{ careers?: number|null, seats?: number|null,
 *   talentStorage?: number|null }} params.usage gezählte Nutzung, null
 *   solange sie nicht sicher bekannt ist
 * @returns {object[]} Deskriptoren
 */
export const describeUsageAlerts = ({
  entitlements,
  isAdmin = false,
  partner,
  usage = {}
}) => {
  const partnerId = partner?.id ?? null
  if (!isUsageAlertEligible({ entitlements, isAdmin, partnerId })) return []

  const plan = entitlements.plan || null
  const limits = Object.fromEntries(
    USAGE_ALERT_RESOURCES.map((resource) => [
      resource,
      limitOf(entitlements, resource)
    ])
  )

  return USAGE_ALERT_RESOURCES.flatMap((resource) => {
    const base = entitlements.limits?.[resource]
    const used = usage[resource] ?? null
    const limit = limits[resource]
    const threshold = base?.enforced
      ? usageAlertThreshold({ limit, used })
      : null

    if (!threshold) return []

    return [
      {
        resource,
        threshold,
        used,
        limit,
        partnerId,
        partnerName: partner?.name || partner?.display_name || 'unbekannt',
        paymentId:
          entitlements.planPayment?.id ?? entitlements.payment?.id ?? null,
        plan: plan ? { id: plan.id, name: plan.name || null } : null,
        limits,
        mode: entitlements.mode,
        isAdmin: !!isAdmin || !!entitlements.isAdmin,
        isSimulated: !!entitlements.isSimulated
      }
    ]
  })
}

/**
 * Dedupe-Key, [STAGE, partner_id, 'usage', resource, threshold, payment_id,
 * limit]. Das Limit gehört dazu, ein neu gebuchtes Kontingent ist neue
 * Information.
 *
 * @param {object} descriptor aus describeUsageAlerts
 * @param {number} [thresholdOverride] Standard ist die Schwelle des
 *   Deskriptors
 */
export const usageAlertKey = (
  { partnerId, resource, threshold, paymentId, limit },
  thresholdOverride = threshold
) =>
  [
    STAGE,
    partnerId,
    'usage',
    resource,
    thresholdOverride,
    paymentId ?? 'none',
    limit
  ].join(':')

// Keys der eigenen und aller höheren Schwellen. Ein Alert „voll" macht den zu
// „bei 80 Prozent" überflüssig, umgekehrt nicht.
const coveringKeys = (descriptor) =>
  THRESHOLD_ORDER.filter((threshold) => threshold >= descriptor.threshold).map(
    (threshold) => usageAlertKey(descriptor, threshold)
  )

export const wasUsageAlertSentRecently = (descriptor, now = Date.now()) =>
  !!descriptor?.partnerId &&
  coveringKeys(descriptor).some((key) => cooldown.wasSentRecently(key, now))

// ------------------------------------------------------------- Formatierung

const RESOURCE_LABEL = {
  careers: 'Stellen',
  seats: 'Zugänge',
  talentStorage: 'Talentspeicher'
}

const percentOf = ({ used, limit }) => Math.floor((used * 100) / limit)

const usageText = ({ used, limit }) =>
  `${formatCount(used)} von ${formatCount(limit)}`

// Bei 80 Prozent nennt der Kopf den echten Wert wie der Satz darunter, sonst
// stünde „bei 80 Prozent" über „(95 Prozent)"
const headlineOf = (descriptor) =>
  descriptor.threshold === USAGE_ALERT_THRESHOLDS.FULL
    ? `${RESOURCE_LABEL[descriptor.resource]} voll`
    : `${RESOURCE_LABEL[descriptor.resource]} bei ${formatCount(
        percentOf(descriptor)
      )} Prozent`

const emojiOf = ({ threshold }) =>
  threshold === USAGE_ALERT_THRESHOLDS.FULL
    ? ':rotating_light:'
    : ':chart_with_upwards_trend:'

// Ein Satz je Ressource und Schwelle, der Kunde fett wie in payment-alert.js
const LEADS = {
  careers: {
    [USAGE_ALERT_THRESHOLDS.WARNING]: ({ name, used, limit, percent }) =>
      `*${name}* hat ${used} von ${limit} Stellen aktiv (${percent} Prozent). ` +
      'Ein guter Zeitpunkt für ein Gespräch über Zusatzstellen oder ein ' +
      'größeres Paket.',
    [USAGE_ALERT_THRESHOLDS.FULL]: ({ name, used, limit }) =>
      `*${name}* hat ${used} von ${limit} Stellen aktiv, das Kontingent ist ` +
      'ausgeschöpft. Weitere Stellen lassen sich erst aktivieren, wenn der ' +
      'Kunde eine Stelle archiviert oder mehr Stellen bucht.'
  },
  seats: {
    [USAGE_ALERT_THRESHOLDS.WARNING]: ({ name, used, limit, percent }) =>
      `*${name}* hat ${used} von ${limit} Zugängen vergeben (${percent} ` +
      'Prozent). Ein guter Zeitpunkt für ein Gespräch über weitere Zugänge ' +
      'oder ein größeres Paket.',
    [USAGE_ALERT_THRESHOLDS.FULL]: ({ name, used, limit }) =>
      `*${name}* hat ${used} von ${limit} Zugängen vergeben, alle Zugänge ` +
      'sind belegt. Neue Teammitglieder kann der Kunde erst einladen, wenn ' +
      'ein Zugang frei wird oder er weitere bucht.'
  },
  talentStorage: {
    [USAGE_ALERT_THRESHOLDS.WARNING]: ({ name, used, limit, percent }) =>
      `*${name}* nutzt ${used} von ${limit} Plätzen im Talentspeicher ` +
      `(${percent} Prozent). Ein guter Zeitpunkt für ein Gespräch über ` +
      'Zusatzstellen oder ein größeres Paket.',
    [USAGE_ALERT_THRESHOLDS.FULL]: ({ name, used, limit }) =>
      `*${name}* nutzt ${used} von ${limit} Plätzen, der Talentspeicher ist ` +
      'voll. Neue Talente werden gespeichert, ihre Ergebnisse sind für den ' +
      'Kunden gesperrt. Frei wird Platz nur durch Löschen, mehr Speicher ' +
      'gibt es über Zusatzstellen oder ein größeres Paket.'
  }
}

export const usageAlertLead = (descriptor) =>
  LEADS[descriptor.resource][descriptor.threshold]({
    name: descriptor.partnerName,
    used: formatCount(descriptor.used),
    limit: formatCount(descriptor.limit),
    percent: formatCount(percentOf(descriptor))
  })

// -------------------------------------------------------------- Slack-Payload

/**
 * Block-Kit-Payload für #vertrieb. text ist der Fallback für Push und
 * Sidebar, Slack zeigt dort nie die Blocks.
 *
 * @param {object} descriptor aus describeUsageAlerts
 */
export const buildUsageAlertPayload = (descriptor) => {
  const emoji = headlineEmoji(emojiOf(descriptor))
  const prefix = stagePrefix()
  const headline = headlineOf(descriptor)
  const usage = usageText(descriptor)

  return {
    text: `${emoji} ${prefix}${headline} für ${descriptor.partnerName} (${usage})`,
    unfurl_links: false,
    unfurl_media: false,
    blocks: [
      {
        type: 'header',
        text: {
          type: 'plain_text',
          text: `${emoji} ${prefix}${headline}`,
          emoji: true
        }
      },
      {
        type: 'section',
        fields: [
          { type: 'mrkdwn', text: `*Kunde*\n${descriptor.partnerName}` },
          { type: 'mrkdwn', text: `*Nutzung*\n${usage}` },
          { type: 'mrkdwn', text: `*Paket*\n${planText(descriptor)}` },
          {
            type: 'mrkdwn',
            text: `*Payment*\n${
              descriptor.paymentId ? `\`${descriptor.paymentId}\`` : 'unbekannt'
            }`
          }
        ]
      },
      {
        type: 'section',
        text: { type: 'mrkdwn', text: usageAlertLead(descriptor) }
      },
      {
        type: 'actions',
        elements: [
          {
            type: 'button',
            action_id: 'open_aivy_os_customer',
            style: 'primary',
            url: getAivyOsCustomerUrl({ partner_id: descriptor.partnerId }),
            text: { type: 'plain_text', text: 'Im Aivy OS öffnen', emoji: true }
          }
        ]
      },
      {
        type: 'context',
        elements: [{ type: 'mrkdwn', text: contextLine(TRIGGER) }]
      }
    ]
  }
}

// ------------------------------------------------------------------ Versand

// Zweite Sicherung neben describeUsageAlerts, falls ein Aufrufer einen
// Deskriptor selbst baut
const isSendable = (descriptor) =>
  descriptor.mode === MODES.ENFORCED &&
  !descriptor.isAdmin &&
  !descriptor.isSimulated &&
  THRESHOLD_ORDER.includes(descriptor.threshold) &&
  isCount(descriptor.limit) &&
  descriptor.limit > 0

/**
 * Schickt den Alert nach #vertrieb, einmal je Key in 30 Tagen, im Browser
 * und über den Log am Partner.
 *
 * Ohne Recht auf den Log (canWriteLog false, etwa VIEWER) wird nichts
 * gesendet und nichts markiert, SKIPPED. Die Dedupe über alle Nutzer hinge
 * sonst nur am Browser.
 *
 * Vor dem Senden markieren, so löst ein zweiter Render oder ein zweiter Tab
 * keinen zweiten Alert aus. Schlägt der Versand fehl, wird die Markierung
 * entfernt, ein späterer Aufruf darf es erneut versuchen. Wann das passiert,
 * entscheidet der Aufrufer (use-usage-alerts.js, einmal je Seitenaufruf).
 *
 * @param {object} descriptor aus describeUsageAlerts
 * @param {{ now?: number, canWriteLog?: boolean, firedById?: string|null }}
 *   [options] canWriteLog heißt, die Person darf Logs am Partner anlegen
 * @returns {Promise<'SENT'|'DUPLICATE'|'SKIPPED'>} wirft bei einem Fehler
 *   des Webhooks, auch wenn Slack mit einem Status außerhalb von 2xx
 *   antwortet
 */
export const maybeSendUsageAlert = async (
  descriptor,
  { now = Date.now(), canWriteLog = false, firedById = null } = {}
) => {
  if (!descriptor?.partnerId || !isSendable(descriptor) || !canWriteLog) {
    return USAGE_ALERT_RESULTS.SKIPPED
  }

  const key = usageAlertKey(descriptor)
  const keys = coveringKeys(descriptor)

  if (keys.some((covering) => cooldown.wasSentRecently(covering, now))) {
    return USAGE_ALERT_RESULTS.DUPLICATE
  }

  cooldown.markSent(key, now)

  let alertedByTeam = false
  try {
    alertedByTeam = await hasRecentPartnerLog({
      partnerId: descriptor.partnerId,
      eventName: PRICING_THRESHOLD_LOG_EVENT,
      sources: keys,
      windowMs: USAGE_ALERT_COOLDOWN_MS,
      now
    })
  } catch {
    // fail-open, die Dedupe pro Browser greift weiter
  }

  if (alertedByTeam) return USAGE_ALERT_RESULTS.DUPLICATE

  try {
    const response = await sendSlackMessage(
      'vertrieb',
      buildUsageAlertPayload(descriptor),
      true
    )
    assertSlackAccepted(response, 'pricing usage alert')
  } catch (err) {
    cooldown.unmark(key, now)
    throw err
  }

  try {
    await writePartnerLog({
      partnerId: descriptor.partnerId,
      eventName: PRICING_THRESHOLD_LOG_EVENT,
      source: key,
      paymentId: descriptor.paymentId,
      firedById,
      data: {
        resource: descriptor.resource,
        threshold: descriptor.threshold,
        limit: descriptor.limit
      },
      windowMs: USAGE_ALERT_COOLDOWN_MS,
      now
    })
  } catch {
    // Ohne Log bleibt nur die Dedupe pro Browser, gesendet ist trotzdem
  }

  return USAGE_ALERT_RESULTS.SENT
}
